import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import { createWorkWeek } from './WorkWeek';
import { createWorkDay } from './WorkDay';
import { createCustomAgenda } from './CustomAgenda';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";
import { Link, FormControl, Select, MenuItem, Box, Button, ButtonGroup, Autocomplete, TextField, InputLabel, ListItemText, Snackbar, Typography, styled, Dialog, DialogTitle, DialogContent, DialogActions, Tooltip, Chip, Portal } from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import ScheduleTaskSessionModal from './Modals/ScheduleTaskSessionModal';
import EditTaskSessionModal from './Modals/EditTaskSessionModal';
import TaskAttendanceModal from './Modals/TaskAttendanceModal';
import ReccurrenceModal from './Modals/RecurrenceModal';
import TaskNotesModal from './Modals/TaskNotesModal';
import axios from 'axios';
import { APIURL } from '../../config';
import CloseIcon from '@mui/icons-material/Close';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import LoopIcon from '@mui/icons-material/Loop';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import IconButton from '@mui/material/IconButton';
import LoadingSkeleton from './LoadingSkeleton';
import './Scheduler.css';
import { useReactToPrint } from 'react-to-print';
import { Grid } from '@mui/material';
import CustomToolbar from './CustomToolbar';

const localizer = momentLocalizer(moment);
const DnDCalendar = withDragAndDrop(Calendar);
const VIEW_STORAGE_KEY = 'serviceProviderSchedulerCurrentView';
const AGENDA_VIEW_STORAGE_KEY = 'serviceProviderSchedulerAgendaView';
const DATE_STORAGE_KEY = 'serviceProviderSchedulerCurrentDate';

const StyledAutocomplete = styled(Autocomplete)({
  '& .MuiAutocomplete-tag': {
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  '& .MuiInputBase-root': {
    flexWrap: 'nowrap',
  },
});

const ServiceProviderScheduler = (props) => {
  const initialCalendarView = (() => {
    if (typeof window === 'undefined') return 'day';
    try {
      return sessionStorage.getItem(VIEW_STORAGE_KEY) || 'day';
    } catch (err) {
      return 'day';
    }
  })();
  const initialAgendaView = (() => {
    if (typeof window === 'undefined') return 'month';
    try {
      const stored = sessionStorage.getItem(AGENDA_VIEW_STORAGE_KEY);
      return stored === 'week' || stored === 'day' || stored === 'month' ? stored : 'month';
    } catch (err) {
      return 'month';
    }
  })();
  const initialCalendarDate = (() => {
    if (typeof window === 'undefined') return new Date();
    try {
      const stored = sessionStorage.getItem(DATE_STORAGE_KEY);
      const parsed = stored ? new Date(stored) : null;
      return parsed && !Number.isNaN(parsed.getTime()) ? parsed : new Date();
    } catch (err) {
      return new Date();
    }
  })();
  const [selectedView, setSelectedView] = useState('resource');
  const [isScheduleTaskSessionModalOpen, setIsScheduleSessionModalOpen] = useState(false); // State for Schedule Task Session modal
  const [isEditTaskSessionModalOpen, setIsEditTaskSessionModalOpen] = useState(false); // State for Assign Customer modal
  const [isTaskAttendanceModalOpen, setIsTaskAttendanceModalOpen] = useState(false); // State for TaskAttendance modal
  const [isRecurrenceModalOpen, setIsRecurrenceModalOpen] = useState(false); // State for Recurrence modal
  const [isTaskNotesModalOpen, setIsTaskNotesModalOpen] = useState(false); // State for Task Notes modal

  const [currentDate, setCurrentDate] = useState(initialCalendarDate);

  const [resources, setResources] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [customers, setCustomers] = useState([]); 
  const [schedules, setSchedules] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [taskTypes, setTaskTypes] = useState([]);

  const [selectedResources, setSelectedResources] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [selectedTaskCustomer, setSelectedTaskCustomer] = useState(null);

  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);
  const [message, setMessage] = useState('');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteTargetEvent, setDeleteTargetEvent] = useState(null);

  const [slotScheduleInfo, setSlotScheduleInfo] = useState({});

  const [dataset, setDataset] = useState([]);

  const [openingTime, setOpeningTime] = useState(new Date(1972, 0, 1, 0, 0, 0, 0));
  const [closingTime, setClosingTime] = useState(new Date(1972, 0, 1, 23, 59, 59, 999));
  const [selectedDays, setSelectedDays] = useState([]);

  const [draggedSchedule, setDraggedSchedule] = useState(null);
  const [view, setView] = useState(initialCalendarView);
  const [agendaView, setAgendaView] = useState(initialAgendaView);

  const [isLoading, setIsLoading] = useState(true);

  const [currentDayColor, setCurrentDayColor] = useState(() => {
    try {
      const s = props.user.comp_settings.find(setting => setting.feature_id === 80);
      if (s) { const p = JSON.parse(s.feature_value); if (p.currentDayColor) return p.currentDayColor; }
    } catch (e) {}
    return '#eaf6ff';
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      sessionStorage.setItem(VIEW_STORAGE_KEY, view);
    } catch (err) {
      // ignore storage errors
    }
  }, [view]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (view !== 'agenda') return;
    try {
      sessionStorage.setItem(AGENDA_VIEW_STORAGE_KEY, agendaView);
    } catch (err) {
      // ignore storage errors
    }
  }, [agendaView, view]);

  const persistCalendarDate = useCallback((dateToStore) => {
    if (typeof window === 'undefined') return;
    try {
      sessionStorage.setItem(DATE_STORAGE_KEY, dateToStore.toISOString());
    } catch (err) {
      // ignore storage errors
    }
  }, []);

  useEffect(() => {
    persistCalendarDate(currentDate);
  }, [currentDate, persistCalendarDate]);
  const [loadedRange, setLoadedRange] = useState({
    start: moment().subtract(1, 'month').startOf('month'),
    end: moment().add(1, 'month').endOf('month')
  });

  const isAdmin = props.user.type === 'Admin';
  const ref = useRef();

  const sessionsByScheduleId = useMemo(() =>
    sessions.reduce((map, s) => {
      if (!map[s.schedule_id]) map[s.schedule_id] = [];
      map[s.schedule_id].push(s);
      return map;
    }, {}),
  [sessions]);

  const customerMap = useMemo(() =>
    customers.reduce((map, c) => { map[c.cus_id] = c; return map; }, {}),
  [customers]);

  const employeeMap = useMemo(() =>
    employees.reduce((map, e) => { map[e.id] = e; return map; }, {}),
  [employees]);

  const resourceMap = useMemo(() =>
    resources.reduce((map, r) => { map[r.resource_id] = r; return map; }, {}),
  [resources]);

  const handlePrint = useReactToPrint({
    content: () => ref.current,
  });

  const handleOpenScheduleTaskSessionModal = () => {
    setIsScheduleSessionModalOpen(true);
  };

  const handleCloseScheduleTaskSessionModal = () => {
    setIsScheduleSessionModalOpen(false);
  };

  const handleOpenEditTaskSessionModal = () => {
    if(props.user.permissions.EditSession)
      setIsEditTaskSessionModalOpen(true);
  };

  const handleCloseEditTaskSessionModal = () => {
    setIsEditTaskSessionModalOpen(false);
  };

  const handleOpenTaskAttendanceModal = () => {
    setIsTaskAttendanceModalOpen(true);
  };

  const handleCloseTaskAttendanceModal = () => {
    setIsTaskAttendanceModalOpen(false);
  };

  const handleOpenRecurrenceModal = () => {
    setIsRecurrenceModalOpen(true);
  };

  const handleCloseRecurrenceModal = () => {
    setIsRecurrenceModalOpen(false);
  };

  const handleOpenTaskNotesModal = () => {
    setIsTaskNotesModalOpen(true);
  };

  const handleCloseTaskNotesModal = () => {
    setIsTaskNotesModalOpen(false);
  };

  const handleNavigate = useCallback((nextDate) => {
    const next = nextDate instanceof Date ? nextDate : new Date(nextDate);
    if (!Number.isNaN(next.getTime())) {
      setCurrentDate(next);
    }
  }, []);

  const getData = async () => {
    try {
      const [resources, employees, customers, schedulesResp, taskTypes] = await Promise.all([
        axios.get(APIURL + '/resources/company/' + props.comp_id),
        axios.get(APIURL + '/employees', { params: { comp_id: props.comp_id } }),
        axios.get(APIURL + '/customers/v2/associated', { params: { company_id: props.comp_id, employee_id: props.user.emp_id, type: props.user.permissions.AdminLevelCustomers ? "Admin": "Manager" } }),
        axios.get(APIURL + '/master_schedule/company/' + props.comp_id),
        axios.get(APIURL + '/dropdowns/dropdownType/taskType', { params: { comp_id: props.comp_id, employeeType: props.user.type } }),
      ]);
      // fetch only sessions for these schedules
      const scheduleList = schedulesResp.data.filter(schedule => schedule.resource_id === null);
      const scheduleIds = scheduleList.map(s => s.schedule_id).join(',');
      let sessData = [];
      if (scheduleIds) {
        // fetch sessions by POST with scheduleIds array
        const sessResp = await axios.post(
          `${APIURL}/sessions/schedule`,
          { scheduleIds: scheduleList.map(s => s.schedule_id) }
        );
        sessData = sessResp.data;
      }
      
      setResources(resources.data);
      setEmployees(employees.data);
      setCustomers(customers.data?.filter(customer =>( customer.cus_status === 'Active' || customer.cus_status === 'Prospect' )));
      setSchedules(scheduleList);
      setSessions(sessData);
      setTaskTypes(taskTypes.data);
      setIsLoading(false);

      // remember what we've fetched
      const initStart = moment().subtract(1, 'month').startOf('month');
      const initEnd = moment().add(1, 'month').endOf('month');
      setLoadedRange({ start: initStart, end: initEnd });      
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  }

  const reloadScheduleData = async () => {
    try {
      const schedules = await axios.get(APIURL + '/master_schedule/company/' + props.comp_id);
      
      const scheduleList = schedules.data.filter(schedule => schedule.resource_id === null);
      setSchedules(scheduleList);
      setSelectedSchedule(scheduleList.find(schedule => schedule.schedule_id === selectedSchedule.schedule_id));
      // reload only sessions for current schedules
      if (scheduleList.length) {
        const scheduleIds = scheduleList.map(s => s.schedule_id);
        const respSessions = await axios.post(
          `${APIURL}/sessions/schedule`,
          { scheduleIds }
        );
        setSessions(respSessions.data);
      } else {
        setSessions([]);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  }

  const reloadSessionData = async () => {
    try {
      if (schedules.length) {
        const scheduleIds = schedules.map(s => s.schedule_id);
        const resp = await axios.post(
          `${APIURL}/sessions/schedule`,
          { scheduleIds }
        );
        setSessions(resp.data);
      } else {
        setSessions([]);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  }
  
  const setData = (filteredSchedules, resource = false) => {
    setDataset(filteredSchedules.map((schedule, index) => ({
      id: index,
      title: schedule.schedule_name,
      start: new Date(schedule.start_time),
      end: new Date(schedule.end_time),
      schedule: schedule,
      tooltip: '',
      resourceId: selectedView === 'employee' ? schedule.employee_ids[0] : undefined,
      isDraggable: schedule.employee_ids.includes(props.user.emp_id) || isAdmin
    })));
  }

  useEffect(() => {
    getData();
  }, [props.selectedProgramId]);

  useEffect(() => {
    if (!isLoading)
      setSelectedView('employee');
  }, [isLoading]);

  useEffect(() => {
    let filteredSchedules = schedules;
    
    switch (selectedView) {
      case 'all':        
        setData(filteredSchedules);
        break;
      case 'employee':
        let filteredEmployees = employees.slice(0, 10);
        {
          const empIdSet = new Set(filteredEmployees.map(e => e.id));
          filteredSchedules = filteredSchedules.filter(schedule => empIdSet.has(schedule.employee_ids[0]));
        }
        setData(filteredSchedules);
        setSelectedEmployees(filteredEmployees);        
        break;
      case 'customer':
        let customer = customers[0];
        if(customer) {
          const custSessions = sessions.filter(session => session.customer_id === customer.cus_id);
          const custScheduleIds = new Set(custSessions.map(s => s.schedule_id));
          filteredSchedules = filteredSchedules.filter(schedule => custScheduleIds.has(schedule.schedule_id));
          setData(filteredSchedules);
          setSelectedCustomers([customer]);
        }
        break;
      default:
        break;
    }
  }, [selectedView]);

  useEffect(() => {
    let filteredSchedules = schedules;
    const filteredEmployees = employees.slice(0, 10);
    const empIdSet = new Set(filteredEmployees.map(e => e.id));
    filteredSchedules = filteredSchedules.filter(schedule => empIdSet.has(schedule.employee_ids[0]));
    setData(filteredSchedules);
    setSelectedEmployees(filteredEmployees);
  }, [employees]);

  useEffect(() => {
    let filteredSchedules = schedules;
    if (selectedResources.length > 0) {
      filteredSchedules = filteredSchedules.filter(schedule => selectedResources.some(resource => resource.resource_id === schedule.resource_id));
      setData(filteredSchedules);
    }
  }, [selectedResources]);

  useEffect(() => {
    let filteredSchedules = schedules;
    if (selectedEmployees.length > 0) {
      const selectedEmpIdSet = new Set(selectedEmployees.map(e => e.id));
      filteredSchedules = filteredSchedules.filter(schedule => selectedEmpIdSet.has(schedule.employee_ids[0]));
      setData(filteredSchedules);
    } else {
      setDataset([]);
    }
  }, [selectedEmployees]);

  useEffect(() => {
    let filteredSchedules = schedules;
    if (selectedCustomers.length > 0) {
      const selectedCusIdSet = new Set(selectedCustomers.map(c => c.cus_id));
      const customerSessions = sessions.filter(session => selectedCusIdSet.has(session.customer_id));
      const customerScheduleIds = new Set(customerSessions.map(s => s.schedule_id));
      filteredSchedules = filteredSchedules.filter(schedule => customerScheduleIds.has(schedule.schedule_id));
      setData(filteredSchedules);
    } else {
      setDataset([]);
    }
  }, [selectedCustomers]);
        
  useEffect(() => {
    if(props.user.comp_settings.find(setting => setting.feature_id === 80)) {
      let schedulerHours = JSON.parse(props.user.comp_settings.find(setting => setting.feature_id === 80).feature_value);
      setOpeningTime(new Date(schedulerHours.openingTime || '1972-01-01T00:00:00.000Z'));
      setClosingTime(new Date(schedulerHours.closingTime || '1972-01-01T23:59:59.999Z'));
      if(schedulerHours.days?.length > 0) {
        setSelectedDays(schedulerHours.days);
      }

    }

  }, [props.user]);

  useEffect(() => {
    axios.get(APIURL + '/config/settings', { params: { comp_id: props.comp_id, feature_id: 80 } })
      .then(res => {
        if (res.data?.length > 0) {
          try {
            const parsed = JSON.parse(res.data[0].feature_value);
            if (parsed.currentDayColor) setCurrentDayColor(parsed.currentDayColor);
          } catch (e) {}
        }
      })
      .catch(() => {});
  }, [props.comp_id]);

  useEffect(() => {
    let filteredSchedules = schedules;
    if(selectedView === 'all') {
      setData(filteredSchedules);
    } else if(selectedView === 'employee') {
      if(selectedEmployees.length > 0) {
        const selectedEmpIdSet = new Set(selectedEmployees.map(e => e.id));
        filteredSchedules = filteredSchedules.filter(schedule => selectedEmpIdSet.has(schedule.employee_ids[0]));
        setData(filteredSchedules);        
      }
    } else if(selectedView === 'customer') {
      if(selectedCustomers.length > 0) {
        const selectedCusIdSet = new Set(selectedCustomers.map(c => c.cus_id));
        const customerSessions = sessions.filter(session => selectedCusIdSet.has(session.customer_id));
        const customerScheduleIds = new Set(customerSessions.map(s => s.schedule_id));
        filteredSchedules = filteredSchedules.filter(schedule => customerScheduleIds.has(schedule.schedule_id));
        setData(filteredSchedules);
      }
    }
  }, [schedules, sessions]);

  useEffect(() => {
    if(props.selectedView){
      setSelectedView(props.selectedView);
    }
  }, [props.selectedView]);

  const displayViewSelect = () => {
    let options;
    let getOptionLabel;
    let label;
    let onChange;
    let value;
  
    switch (selectedView) {
      case 'employee':
        options = employees;
        getOptionLabel = (option) => option.employee_name;
        label = 'Employee';
        onChange = (event, newValue) => setSelectedEmployees(newValue);
        value = selectedEmployees;
        break;
      case 'customer':
        options = customers;
        getOptionLabel = (option) => option.cus_name;
        label = 'Customer';
        onChange = (event, newValue) => setSelectedCustomers(newValue);
        value = selectedCustomers;
        break;
      default:
        return null;
    }
  
    return (
      <StyledAutocomplete
        size='small'
        options={options}
        getOptionLabel={getOptionLabel}
        style={{ width: 'auto', maxWidth: '1000px' }}
        multiple
        renderInput={(params) => <TextField {...params} label={label} />}
        onChange={onChange}
        renderOption={(props, option) => {
          let key = null;
          if (option.resource_id) {
            key = option.resource_id;
          } else if (option.id) {
            key = option.id;
          } else if (option.cus_id) {
            key = option.cus_id;
          }
          return (
            <li {...props} key={key}>
              <ListItemText primary={getOptionLabel(option)} />
            </li>
          )
        }}
        value={value}
        sx={{ width: value && value.length > 0 ? 300 + value.length * 30 : 300, minWidth: 300, mr: 2 }}
      />
    );
  }
  
  const onSelectEvent = (event) => {
    setSelectedSchedule(event.schedule);
    handleOpenEditTaskSessionModal();
  }

  const onSelectSlot = (slotInfo) => {
    if(slotInfo.action === 'select' && moment(slotInfo.start).isSame(slotInfo.end, 'day')) {
      setSlotScheduleInfo({
        startTime: slotInfo.start,
        endTime: slotInfo.end,
        resource: selectedResources.length > 0 ? selectedResources[0] : undefined
      });
      handleOpenScheduleTaskSessionModal();
    }
    
  }

  const addLineBreaks = (text, maxLineLength) => {
    const words = text.split(' ');
    let result = '';
    let line = '';
  
    for (const word of words) {
      if ((line + word).length > maxLineLength) {
        result += line + '\n\t';
        line = word + ' ';
      } else {
        line += word + ' ';
      }
    }
  
    return result + line;
  }
  
  const assignedCustomers = (event) => {
    const scheduledSessions = sessionsByScheduleId[event.schedule.schedule_id] || [];
    const assignedCusts = scheduledSessions.map(s => customerMap[s.customer_id]).filter(Boolean);
    const customerNames = assignedCusts.map(customer => customer.cus_name).join('\n\t');
    
    const notes = event.schedule.notes ? addLineBreaks(event.schedule.notes, 40) : '';
  
    return event.title + '\n' + event.tooltip + '\n' + 'Employees: \n\t' + event.schedule.employee_ids.map(employee_id => employeeMap[employee_id]?.employee_name).join('\n\t') + '\n' +
    'Assigned Customers: \n\t' + (assignedCusts.length > 0 ? customerNames : 'None') + '\nNotes:' + (notes ? '\n\t' + notes : '');
  }

  const onEventDrop = useCallback(async ({ event, start, end, resourceId }) => {
    const originalEvent = { ...event };
    event.start = start;
    event.end = end;

    if(resourceId !== event.resourceId && resourceId) {
      return;
    }

    let updatedSchedule = {
      ...event.schedule,
      date: moment(start).format('YYYY-MM-DD'),
      start_time: start,
      end_time: end
    }

    let schedulesCopy = [...schedules];
    let scheduleIndex = schedulesCopy.findIndex(schedule => schedule.schedule_id === event.schedule.schedule_id);
    schedulesCopy[scheduleIndex] = updatedSchedule;
    setSchedules(schedulesCopy);

    await axios.put(APIURL + '/master_schedule/' + event.schedule.schedule_id, updatedSchedule)
      .catch(error => {
        console.error("Failed to update event", error);
        setDataset((currentEvents) => {
          return currentEvents.map(evt => {
            if (evt.id === event.id) {
              return { ...evt, start: originalEvent.start, end: originalEvent.end };
            }
            return evt;
          });
        });
        setSchedules((currentSchedules) => {
          return currentSchedules.map(schedule => {
            if (schedule.schedule_id === event.schedule.schedule_id) {
              return { ...schedule, date: moment(originalEvent.start).format('YYYY-MM-DD'), start_time: originalEvent.start, end_time: originalEvent.end };
            }
            return schedule;
          });
        });
      });
  }, [dataset, schedules]);

  const onEventResize = useCallback(({ event, start, end }) => {
    const originalStart = event.start;
    const originalEnd = event.end;
    event.start = start;
    event.end = end;

    let updatedSchedule = {
      ...event.schedule,
      date: moment(start).format('YYYY-MM-DD'),
      start_time: start,
      end_time: end
    }

    let schedulesCopy = [...schedules];
    let scheduleIndex = schedulesCopy.findIndex(schedule => schedule.schedule_id === event.schedule.schedule_id);
    schedulesCopy[scheduleIndex] = updatedSchedule;
    setSchedules(schedulesCopy);
    
    axios.put(APIURL + '/master_schedule/' + event.schedule.schedule_id, updatedSchedule)
      .catch(error => {
        console.error("Failed to update event duration", error);
        setDataset((currentEvents) => {
          return currentEvents.map(evt => {
            if (evt.id === event.id) {
              return { ...evt, start: originalStart, end: originalEnd };
            }
            return evt;
          });
        });
        setSchedules((currentSchedules) => {
          return currentSchedules.map(schedule => {
            if (schedule.schedule_id === event.schedule.schedule_id) {
              return { ...schedule, date: moment(originalStart).format('YYYY-MM-DD'), start_time: originalStart, end_time: originalEnd };
            }
            return schedule;
          });
        });
      });
  }, [dataset, schedules]);

  const dragFromOutsideItem = useCallback(() => {
    return draggedSchedule;
  }, [draggedSchedule]);

  const canDragEvent = useCallback((event) => {
    const isWeekView = view === 'week';
    return event?.isDraggable && view !== 'month' && (!isWeekView || !event?.allDay);
  }, [view]);

  const canResizeEvent = useCallback((event) => {
    if (!event?.isDraggable || view === 'month') {
      return false;
    }

    if (view !== 'week') {
      return true;
    }

    const sameDay = event?.start && event?.end && event.start.getDate() === event.end.getDate() && event.start.getMonth() === event.end.getMonth() && event.start.getFullYear() === event.end.getFullYear();
    return !event?.allDay && sameDay;
  }, [view]);

  const CustomEvent = ({ event, actionsOnly }) => {
    const scheduledSessions = sessionsByScheduleId[event.schedule.schedule_id] || [];
    const assignedCustomers = scheduledSessions.map(s => customerMap[s.customer_id]).filter(Boolean);

    let style = view !== 'agenda' ? {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      color: '#fff',
      padding: '2px 4px',
      borderRadius: '2px',
      fontSize: '1rem',
    } : {};
    
    if(view !== 'agenda' && (!event.schedule.employee_ids || event.schedule.employee_ids.length === 0)) {
      style.color = '#ff9800';
    }

    // In agenda view, if actionsOnly is true, render only the action icons (stacked vertically)
    if (view === 'agenda' && actionsOnly === true) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0px' }}>
          {event.isDraggable && (
            <>
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedSchedule(event.schedule);
                  handleOpenRecurrenceModal();
                }}
                size="small"
                sx={{ height: 28, width: 28, color: '#444' }}
              >
                <LoopIcon sx={{ fontSize: 20 }} />
              </IconButton>
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedSchedule(event.schedule);
                  handleOpenTaskAttendanceModal();
                }}
                size="small"
                sx={{ height: 28, width: 28, color: '#444' }}
              >
                <PendingActionsIcon sx={{ fontSize: 18 }} />
              </IconButton>
              <IconButton
                onClick={(e) => { e.stopPropagation(); handleEventDelete(e, event); }}
                size="small"
                sx={{ height: 28, width: 28, color: '#444' }}
              >
                <DeleteOutlineIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </>
          )}
        </Box>
      );
    }

    // In agenda view, if actionsOnly is false, render the event content (no action icons)
    if (view === 'agenda' && actionsOnly === false) {
      const locationName = event.schedule.resource_id ? resourceMap[event.schedule.resource_id]?.resource_name : '';

      return (
        <div style={{ display: 'flex', flexDirection: 'row', lineHeight: '1.8', alignItems: 'flex-start', gap: '12px' }}>
          {/* Event Details: title, location, notes */}
          <Box sx={{ flex: '0 0 22%', display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <Typography sx={{ fontWeight: 'bold', fontSize: '1.05rem', lineHeight: 1.4 }}>{event.title}</Typography>
            {locationName && (
              <Typography variant="body2" sx={{ color: '#999', fontSize: '0.88rem', lineHeight: 1.4, display: 'flex', alignItems: 'center' }}>
                <LocationOnIcon sx={{ fontSize: 16, mr: '2px' }} /> {locationName}
              </Typography>
            )}
            {event.schedule.notes && (
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontSize: '0.88rem', color: '#555', mt: 0.3 }}>{event.schedule.notes}</Typography>
            )}
          </Box>
          {/* Employees */}
          <Box sx={{ flex: '0 0 20%', minWidth: 0 }}>
            <Typography sx={{ fontWeight: 'bold', fontSize: '0.9rem', mb: 0.5 }}>Employees:</Typography>
            {event.schedule.employee_ids.map(employee_id => {
              const emp = employeeMap[employee_id];
              return emp ? (
                <Box key={employee_id} sx={{ display: 'flex', alignItems: 'center', gap: '6px', mb: 0.4 }}>
                  <AccountCircleIcon sx={{ fontSize: 20, color: '#546e7a' }} />
                  <Typography sx={{ fontSize: '0.9rem' }}>{emp.employee_name}</Typography>
                </Box>
              ) : null;
            })}
          </Box>
          {/* Assigned Customers */}
          <Box sx={{ flex: '1 1 0', display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <Typography sx={{ fontWeight: 'bold', fontSize: '0.9rem', mb: 0.5 }}>Assigned Customers (confirmed):</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px', justifyContent: 'flex-start' }}>
            {assignedCustomers.map(customer => {
              const session = scheduledSessions.find(s => s.customer_id === customer.cus_id && s.schedule_id === event.schedule.schedule_id);
              const checkedIn = !!session?.check_in_time || !!session?.is_complete;
              const clickable = !!session?.check_in_time && customer.cus_status === 'Active';

              const chipSx = checkedIn
                ? { bgcolor: '#e8f5e9', borderColor: '#66bb6a' }
                : {};

              return (
                <Chip
                  key={customer.cus_id}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                      <span>{customer.cus_name}</span>
                      {!!session?.is_complete && (
                        <Tooltip title="Forms Completed">
                          <CheckCircleIcon color="success" fontSize="small" style={{ marginLeft: 5 }} />
                        </Tooltip>
                      )}
                    </Box>
                  }
                  size="small"
                  variant="outlined"
                  onClick={clickable && props.user.permissions.AddSessionNote ? (e) => {
                    e.stopPropagation();
                    setSelectedSchedule(event.schedule);
                    setSelectedTaskCustomer(customer);
                    handleOpenTaskNotesModal();
                  } : undefined}
                  sx={{
                    fontSize: '0.85rem',
                    height: 'auto',
                    py: '3px',
                    cursor: clickable ? 'pointer' : 'default',
                    ...chipSx,
                    '& .MuiChip-label': { px: '8px', display: 'flex', alignItems: 'center' },
                  }}
                />
              );
            })}
            </Box>
          </Box>
        </div>
      );
    }

    // Non-agenda views (month, week, day) — original rendering
    return (
      <div style={style}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', flexBasis: '100%', flexDirection: 'row', flexWrap: view !== 'month' ? 'wrap' : 'nowrap', overflow: 'hidden' }}>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{event.title}</span>
          {event.isDraggable &&
            <Box>
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedSchedule(event.schedule);
                  handleOpenRecurrenceModal();
                }}
                size="small"
                style={{
                  height: '24px',
                  width: '24px',
                  marginLeft: 'auto',
                  color: 'rgba(255, 255, 255, 0.8)',
                }}
              >
                <LoopIcon fontSize="small" />
              </IconButton>
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedSchedule(event.schedule);
                  handleOpenTaskAttendanceModal();
                }}
                size="small"
                style={{
                  height: '24px',
                  width: '24px',
                  marginLeft: 'auto',
                  color: 'rgba(255, 255, 255, 0.8)',
                }}
              >
                <PendingActionsIcon fontSize="small" />
              </IconButton>
              <IconButton
                onClick={(e) => { e.stopPropagation(); handleEventDelete(e, event); }}
                size="small"
                style={{
                  height: '24px',
                  width: '24px',
                  marginLeft: 'auto',
                  color: 'rgba(255, 255, 255, 0.8)',
                }}
              >        
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box> 
          } 
        </Box>
      </div>
    )
  };

  const handleEventDelete = async (e, event) => {
    e.stopPropagation();

    setDeleteTargetEvent(event);
    setIsDeleteModalOpen(true);
  }

  const handleConfirmDelete = async (isRecurrence) => {
    if (!deleteTargetEvent) return;
    try {
      await axios.delete(APIURL + '/master_schedule/' + deleteTargetEvent.schedule.schedule_id, {
        data: { reccurence: isRecurrence }
      });
      setMessage('Schedule deleted successfully');
      setSuccess(true);
      reloadScheduleData();
    } catch (err) {
      setMessage('Error deleting schedule');
      setError(true);
    } finally {
      setIsDeleteModalOpen(false);
      setDeleteTargetEvent(null);
    }
  };

  const nextAgendaView = (shouldSet = true) => {
    let newView;
    switch (agendaView) {
      case 'month':
        newView = 'week';
        break;
      case 'week':
        newView = 'day';
        break;
      case 'day':
        newView = 'month';
        break;
      default:
        newView = 'month';
        break;
    }
    if (shouldSet) {
      setAgendaView(newView);
    }
    return newView;
  };

  const prevAgendaView = (shouldSet = true) => {
    let newView;
    switch (agendaView) {
      case 'month':
        newView = 'day';
        break;
      case 'week':
        newView = 'month';
        break;
      case 'day':
        newView = 'week';
        break;
      default:
        newView = 'month';
        break;
    }
    if (shouldSet) {
      setAgendaView(newView);
    }
    return newView;
  };

  const handleRangeChange = async (range) => {
    let rangeStart = Array.isArray(range) ? moment(range[0]) : moment(range.start);
    let rangeEnd = Array.isArray(range) ? moment(range[range.length - 1]) : moment(range.end);
    // previous threshold
    if (rangeStart.isBefore(moment(loadedRange.start).add(1, 'month'), 'day')) {
      const mStart = moment(loadedRange.start).subtract(1, 'month').startOf('month');
      const mEnd = moment(loadedRange.start).subtract(1, 'month').endOf('month');
      const resp = await axios.get(`${APIURL}/master_schedule/company/${props.comp_id}`, { params: { startDate: mStart.format('YYYY-MM-DD'), endDate: mEnd.format('YYYY-MM-DD') } });
      setSchedules(prev => [...resp.data.filter(s => !prev.some(p => p.schedule_id === s.schedule_id)), ...prev]);
      setLoadedRange(r => ({ start: mStart, end: r.end }));
      const newScheduleIds = resp.data.map(s => s.schedule_id);
      if (newScheduleIds.length) {
        const sessResp = await axios.post(
          `${APIURL}/sessions/schedule`,
          { scheduleIds: newScheduleIds }
        );
        setSessions(prev => { const ids = new Set(prev.map(s=>s.session_id)); return [...sessResp.data.filter(s=>!ids.has(s.session_id)), ...prev]; });
      }
    }
    // next threshold
    if (rangeEnd.isAfter(moment(loadedRange.end).subtract(1, 'month'), 'day')) {
      const mStart = moment(loadedRange.end).add(1, 'month').startOf('month');
      const mEnd = moment(loadedRange.end).add(1, 'month').endOf('month');
      const resp = await axios.get(`${APIURL}/master_schedule/company/${props.comp_id}`, { params: { startDate: mStart.format('YYYY-MM-DD'), endDate: mEnd.format('YYYY-MM-DD') } });
      setSchedules(prev => [...prev, ...resp.data.filter(s => !prev.some(p => p.schedule_id === s.schedule_id))]);
      setLoadedRange(r => ({ start: r.start, end: mEnd }));
      const nextScheduleIds = resp.data.map(s => s.schedule_id);
      if (nextScheduleIds.length) {
        const sessResp = await axios.post(
          `${APIURL}/sessions/schedule`,
          { scheduleIds: nextScheduleIds }
        );
        setSessions(prev => { const ids = new Set(prev.map(s=>s.session_id)); return [...prev, ...sessResp.data.filter(s=>!ids.has(s.session_id))]; });
      }
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <style>{`#service-provider-calendar .rbc-today { background-color: ${currentDayColor} !important; }`}</style>
      {isLoading ? <LoadingSkeleton /> :
        <Box id="service-provider-calendar" sx={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column', m: "10px" }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: "10px",
            }}
          >
            {/* Left-aligned FormControl */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <FormControl fullWidth size="small" sx={{ width: '200px', mr: 2 }}>
                <InputLabel id="view-select-label">View</InputLabel>
                <Select
                  value={selectedView}
                  onChange={(e) => setSelectedView(e.target.value)}
                  displayEmpty
                >
                  { isAdmin &&
                    <MenuItem value="all">All</MenuItem>
                  }
                  <MenuItem value="employee">Employee</MenuItem>
                  <MenuItem value="customer">Customer</MenuItem>
                </Select>
              </FormControl>
              {(selectedView  === 'customer' || (selectedView === 'employee' && isAdmin)) && displayViewSelect()}
            </Box>

            {/* Right-aligned ButtonGroup */}
            <ButtonGroup variant="contained" sx={{ mr: "20px" }}>
              <Button onClick={handlePrint}>Print</Button>
              {props.user.permissions.ScheduleSession && <Button onClick={handleOpenScheduleTaskSessionModal}>Schedule Session</Button>}
            </ButtonGroup>
          </Box>
          <Box ref={ref} sx={{ display: 'flex', justifyContent: 'center', height: '100%', maxWidth: '100%', mr: '20px' }}>
            <Box sx={{ height: '100% !important', width: '100% !important' }}>
              <DnDCalendar
                components={{
                  event: (props) => <CustomEvent {...props} />,
                  toolbar: (props) => <CustomToolbar {...props} nextAgendaView={nextAgendaView} prevAgendaView={prevAgendaView} />,
                }}
                localizer={localizer}
                events={dataset}
                startAccessor="start"
                endAccessor="end"
                tooltipAccessor={(event) => assignedCustomers(event)}
                onSelectEvent={onSelectEvent}
                onSelectSlot={onSelectSlot}
                selectable="ignoreEvents"
                resources={selectedView === 'employee' && selectedEmployees.length > 0 ? selectedEmployees : undefined}
                resourceIdAccessor={(r) => r.id}
                resourceTitleAccessor={(r) => r.employee_name}
                min={openingTime}
                max={closingTime}
                views={{
                  month: true,
                  week:  selectedDays.length > 0 ? createWorkWeek(selectedDays) : true,
                  day:  selectedDays.length > 0 ? createWorkDay(selectedDays) : true,
                  agenda: createCustomAgenda(agendaView)
                }}
                handleDragStart={(event) => setDraggedSchedule(event)}
                onDropFromOutside={({ start, end, allDay }) => onEventDrop({event: draggedSchedule, start: start, end: end, resourceId: null})}
                onEventDrop={onEventDrop}
                onEventResize={onEventResize}
                draggableAccessor={canDragEvent}
                resizableAccessor={canResizeEvent}
                dragFromOutsideItem={dragFromOutsideItem}
                resizable
                popup
                view={view}
                onRangeChange={handleRangeChange}
                onView={(nextView) => setView(nextView)}
                date={currentDate}
                defaultDate={initialCalendarDate}
                onNavigate={handleNavigate}
                messages={{
                  noEventsInRange: agendaView === 'month' ? 'No events this month' : agendaView === 'week' ? 'No events this week' : 'No events today',
                }}
                formats={{
                  agendaDateFormat: (date) => moment(date).format('dddd, MMM DD'),
                }}
                style={{ minHeight: '550px', height: 'calc(100vh - ' + (props.headerHeight ? props.headerHeight : 170) + 'px)' }}
              />
            </Box>
          </Box>
          <ReccurrenceModal 
            open={isRecurrenceModalOpen}
            onClose={handleCloseRecurrenceModal}
            schedule={selectedSchedule}
            sessions={sessions}
            onSuccess={(message) => {setSuccess(true); setMessage(message)}}
            onError={(message) => {setError(true); setMessage(message)}}
            reloadScheduleData={reloadScheduleData}
            reloadSessionData={reloadSessionData}
            comp_id={props.comp_id}
            user={props.user}
          />
          <TaskNotesModal
            open={isTaskNotesModalOpen}
            onClose={handleCloseTaskNotesModal}
            schedule={selectedSchedule}
            customer={selectedTaskCustomer}
            sessions={sessions}
            onSuccess={(message) => {setSuccess(true); setMessage(message)}}
            onError={(message) => {setError(true); setMessage(message)}}
            comp_id={props.comp_id}
            user={props.user}
            onFormsUpdated={reloadSessionData}
          />
          <ScheduleTaskSessionModal
            open={isScheduleTaskSessionModalOpen}
            onClose={handleCloseScheduleTaskSessionModal}
            employees={employees}
            customers={customers}
            taskTypes={taskTypes}
            user={props.user}
            comp_id={props.comp_id}
            reloadScheduleData={reloadScheduleData}
            reloadSessionData={reloadSessionData}
            slotScheduleInfo={slotScheduleInfo}
            onSuccess={(message) => {setSuccess(true); setMessage(message)}}
            onError={(message) => {setError(true); setMessage(message)}}
            isAdmin={isAdmin}
          />
          <EditTaskSessionModal 
            open={isEditTaskSessionModalOpen} 
            onClose={handleCloseEditTaskSessionModal}
            schedule={selectedSchedule}
            customers={customers}
            employees={employees}
            sessions={sessions}
            resources={resources}
            reloadSessionData={reloadSessionData}
            reloadScheduleData={reloadScheduleData}
            onSuccess={(message) => {setSuccess(true); setMessage(message)}}
            onError={(message) => {setError(true); setMessage(message)}}
            isAdmin={isAdmin}
            user={props.user}
            comp_id={props.comp_id}
          />
          <TaskAttendanceModal
            open={isTaskAttendanceModalOpen}
            onClose={handleCloseTaskAttendanceModal}
            schedule={selectedSchedule}
            sessions={sessions}
            customers={customers}
            taskTypes={taskTypes}
            resources={resources}
            reloadSessionData={reloadSessionData}
            onSuccess={(message) => {setSuccess(true); setMessage(message)}}
            onError={(message) => {setError(true); setMessage(message)}}
            user={props.user}
            comp_id={props.comp_id}
          />
          <Portal>
            <Snackbar anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} open={success} autoHideDuration={6000} onClose={()=>{setSuccess(false)}} sx={{ zIndex: 9999 }}>
              <MuiAlert onClose={()=>{setSuccess(false)}} severity="success">
                  {message}
              </MuiAlert>
            </Snackbar>
            <Snackbar anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} open={error} autoHideDuration={6000} onClose={()=>{setError(false)}} sx={{ zIndex: 9999 }}>
                <MuiAlert onClose={()=>{setError(false)}} severity="error">
                    {message}
                </MuiAlert>
            </Snackbar>
          </Portal>
        </Box>
      }
    </Box>
  );
};

export default ServiceProviderScheduler;
